<template>
  <div>
    <BContainer>
      <ProgramMenu class="mb-3" />

      <BRow v-if="status === 'unauthenticated'" class="g-3 mt-4 mb-4 align-items-center">
        <BCol md="8">
          <h2 class="mb-3">
            {{ t('Parallel program') }}
          </h2>
          <p class="mb-3 parallel-description">
            {{ t('Parallel program description') }}
          </p>
          <p class="mb-0 fw-semibold">
            {{ t('To see the full programme, please log in or create an account') }}
          </p>
        </BCol>

        <BCol md="4">
          <RoundedPanel :header="t('Sign in')">
            <LoginForm :redirect-success="localePath('/program/parallel/')" />
            <hr class="my-4" />
            <div class="mb-3">
              {{ t("Don't have an account on the website yet? Complete the registration process") }}.
            </div>
            <BButton variant="dark" class="w-100" :to="localePath('/accounts/signup/')">
              {{ t('Sign up') }}
            </BButton>
          </RoundedPanel>
        </BCol>
      </BRow>

      <template v-if="status === 'authenticated'">
        <BSpinner v-if="pending && events.length === 0" type="grow" :label="t('Loading')" />
        <div v-if="welcomeTxt" class="mb-4" v-html="welcomeTxt.txt ?? ''" />
        <EventList
          :events="events"
          :group_by="false"
          :event-list-path="localePath('/program/parallel/')"
        />
      </template>
    </BContainer>

    <BOffcanvas
      v-model="showEvent"
      :no-footer="true"
      class="interface_sidebar"
      placement="end"
      shadow="lg"
      body-scrolling
      lazy
    >
      <div v-if="currentEventPending">
        <BSpinner small /> {{ t('Loading') }}...
      </div>
      <EventDetail v-else-if="currentEvent" :event="currentEvent" />
    </BOffcanvas>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { definePageMeta, navigateTo, useAsyncData, useHead, useLocalePath, useNuxtApp, useRoute } from '#imports'
import { useI18n } from 'vue-i18n'
import LoginForm from '~/components/auth/LoginForm.vue'
import RoundedPanel from '~/components/global/RoundedPanel.vue'
import ProgramMenu from '~/components/site/program/ProgramMenu.vue'
import EventDetail from '~/components/vip/program/EventDetail.vue'
import EventList from '~/components/vip/program/EventList.vue'

interface WelcomeTxt {
  txt?: string | null
}

const { t, locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()
const { $apiFetch } = useNuxtApp()
const { status } = useAuth()

const { data, pending } = await useAsyncData(
  'program-parallel-page',
  async () => {
    if (status.value !== 'authenticated') {
      return { events: [] as unknown[], welcome_txt: null as WelcomeTxt | null }
    }
    try {
      const [events, welcome_txt] = await Promise.all([
        $apiFetch(`/${locale.value}/api/vip/parallel_events/`) as Promise<unknown[]>,
        $apiFetch(`/${locale.value}/api/vip/txt/parallel/`) as Promise<WelcomeTxt>,
      ])
      return { events, welcome_txt }
    } catch (e) {
      console.error(e)
      return {
        events: [] as unknown[],
        welcome_txt: null as WelcomeTxt | null,
      }
    }
  },
  { watch: [locale, status] },
)

const events = computed(() => data.value?.events ?? [])
const welcomeTxt = computed(() => data.value?.welcome_txt ?? null)

const currentEventId = computed(() => {
  const q = route.query.event
  if (q !== undefined && q !== null && q !== '') {
    return String(Array.isArray(q) ? q[0] : q)
  }
  const raw = route.params.id
  if (raw === undefined || raw === null || raw === '') return null
  return String(Array.isArray(raw) ? raw[0] : raw)
})

const showEvent = computed({
  get: () => currentEventId.value !== null,
  set: (open: boolean) => {
    if (!open && currentEventId.value !== null) {
      void navigateTo({ path: localePath('/program/parallel/'), query: {} })
    }
  },
})

const { data: currentEvent, pending: currentEventPending } = await useAsyncData(
  'program-parallel-current-event',
  async () => {
    const id = currentEventId.value
    if (!id || status.value !== 'authenticated') return null
    return $apiFetch(`/${locale.value}/api/vip/parallel_events/${id}/`) as Promise<Record<string, unknown> | null>
  },
  { watch: [locale, status, currentEventId] },
)

useHead(() => ({
  title: t('Parallel program'),
}))

definePageMeta({
  layout: 'site-layout-content',
  auth: 'guest',
  path: '/program/parallel/:id(\\d+)?',
})
</script>

<style scoped lang="scss">
.parallel-description {
  max-width: 42rem;
}
</style>

<i18n lang="json">
{
  "en": {
    "Loading": "Loading",
    "Parallel program": "Parallel program",
    "Parallel program description": "The Cosmoscow Parallel Programme is a curated selection of exhibitions, concerts, lectures and special projects taking place at our partners' venues during the fair. We've gathered the best of what's happening in the city, so that your visit to Cosmoscow becomes a full journey through contemporary art.",
    "To see the full programme, please log in or create an account": "To see the full programme, please log in or create an account.",
    "Don't have an account on the website yet? Complete the registration process": "Don't have an account on the website yet? Complete the registration process",
    "Sign up": "Sign up",
    "Sign in": "Sign in"
  },
  "ru": {
    "Loading": "Загрузка",
    "Parallel program": "Параллельная программа",
    "Parallel program description": "Параллельная программа Cosmoscow — это авторская подборка выставок, концертов, лекций и специальных проектов, которые в дни ярмарки проходят на площадках наших партнёров. Мы собрали лучшее, что происходит в городе, чтобы визит на Cosmoscow превратился в полноценное путешествие по современному искусству.",
    "To see the full programme, please log in or create an account": "Чтобы увидеть полную программу, войдите в аккаунт или зарегистрируйтесь.",
    "Don't have an account on the website yet? Complete the registration process": "Ещё нет учётной записи на сайте? Пройдите регистрацию",
    "Sign up": "Зарегистрироваться",
    "Sign in": "Вход"
  }
}
</i18n>
